// frontend/packages/business-console/src/pages/LeadForm.jsx
//
// "Lead form" (design handoff Page 7, the public side). The front door
// for new work: how many visitors start the public contact/estimate
// form vs finish, where the funnel leaks, and what each form asks for.
//
// A tenant can run more than one public form (e.g. a contact form plus a
// separate estimate form), so this lists ALL active forms with a picker;
// the selected form drives the field schema + its own funnel. The big
// visit -> submit funnel at the top stays a site-wide overview, but each
// form now shows its OWN started / leads / finish rate: the form_field_focus
// event is keyed by form slug, so starts join to that form's authoritative
// leads (discovery 14, W1b).
//
// Read-only by decision: submissions live in the Inbox (this page links
// there), and edits to the live form happen in the Tools admin surface
// (D24, the public form is config-filled, not built in the Console).
//
// Data: GET /v2/lead-form/forms (all active form schemas) + GET
// /v2/visitors/funnel (site-wide funnel) + GET /v2/visitors/form-insights
// (per-form funnel: starts, leads, guarded conversion). `?demo=1` shows
// fixtures for screenshots.

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import {
  useTokens,
  Surface,
  StatusPill,
  GradientHeroCard,
  PageHeader,
} from '../components/primitives';
import { getLeadForms } from '../services/leadFormService';
import LeadTrendsCard from '../components/leadForm/LeadTrendsCard';
import {
  getVisitorsFunnel,
  getFormInsights,
} from '../services/visitorsService';

const DAY_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 28, label: 'Last 28 days' },
  { value: 90, label: 'Last 90 days' },
];

const FIELD_TYPE_LABEL = {
  text: 'Text',
  email: 'Email',
  tel: 'Phone',
  textarea: 'Long text',
  select: 'Dropdown',
  checkbox: 'Checkbox',
  hidden: 'Hidden',
  file: 'File upload',
};

function readDemoFlag(searchString) {
  const params = new URLSearchParams(searchString || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

const DEMO = {
  funnel: {
    sessions: 3240,
    scrolled75: 1850,
    formStarts: 142,
    formAttempts: 71,
    formSubmits: 51,
  },
  forms: [
    {
      slug: 'estimate',
      name: 'Request an estimate',
      status: 'active',
      fieldCount: 6,
      requiredCount: 5,
      attachmentsEnabled: true,
      fields: [
        { key: 'name', label: 'Your name', type: 'text', required: true },
        { key: 'email', label: 'Email', type: 'email', required: true },
        { key: 'phone', label: 'Phone', type: 'tel', required: true },
        {
          key: 'service',
          label: 'Service needed',
          type: 'select',
          required: true,
        },
        {
          key: 'details',
          label: 'Tell us about the job',
          type: 'textarea',
          required: true,
        },
        { key: 'photos', label: 'Photos', type: 'file', required: false },
      ],
    },
    {
      slug: 'contact',
      name: 'Contact us',
      status: 'active',
      fieldCount: 3,
      requiredCount: 3,
      attachmentsEnabled: false,
      fields: [
        { key: 'name', label: 'Your name', type: 'text', required: true },
        { key: 'email', label: 'Email', type: 'email', required: true },
        { key: 'message', label: 'Message', type: 'textarea', required: true },
      ],
    },
  ],
  insights: {
    windowDays: 30,
    forms: [
      {
        formSlug: 'estimate',
        leadsWindow: 39,
        leadsTotal: 318,
        startsWindow: 52,
        conversionPct: 75,
        lastSubmissionAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
      },
      {
        formSlug: 'contact',
        leadsWindow: 12,
        leadsTotal: 96,
        startsWindow: 48,
        conversionPct: 25,
        lastSubmissionAt: new Date(Date.now() - 26 * 3600 * 1000).toISOString(),
      },
    ],
  },
};

function pct(part, whole) {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

// Short relative-ish label for the last submission time.
function fmtLastSubmission(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function FunnelBar({ label, value, max, color }) {
  const t = useTokens();
  const width = max > 0 ? Math.max(3, Math.round((value / max) * 100)) : 0;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography
          sx={{ fontSize: 12.5, color: t.textSecondary, fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: t.text }}>
          {value.toLocaleString()}
          {max > 0 && max !== value ? (
            <Box component="span" sx={{ color: t.faint, fontWeight: 600 }}>
              {' '}
              · {pct(value, max)}%
            </Box>
          ) : null}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 8,
          bgcolor: t.hairline,
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{ width: `${width}%`, height: '100%', bgcolor: color || t.blue }}
        />
      </Box>
    </Box>
  );
}

// Small per-form stat (all-time leads + last submission).
function LeadStat({ value, caption, t }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 900,
          letterSpacing: '-0.5px',
          color: t.text,
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: t.faint }}>{caption}</Typography>
    </Box>
  );
}

export default function LeadForm() {
  const t = useTokens();
  const navigate = useNavigate();
  const location = useLocation();
  const { business } = useContext(BusinessContext);
  const basePath = business?.slug ? `/business/${business.slug}` : '';
  const isDemo = useMemo(
    () => readDemoFlag(location.search),
    [location.search]
  );

  const [days, setDays] = useState(28);
  const [funnel, setFunnel] = useState(null);
  const [formList, setFormList] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (isDemo) {
      setFunnel(DEMO.funnel);
      setFormList({ forms: DEMO.forms });
      setInsights(DEMO.insights);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    (async () => {
      const [fu, list, ins] = await Promise.all([
        getVisitorsFunnel(days).catch(() => null),
        getLeadForms().catch(() => ({ forms: [] })),
        getFormInsights(days).catch(() => null),
      ]);
      if (cancelled) return;
      setFunnel(fu);
      setFormList(list);
      setInsights(ins);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [days, isDemo]);

  // Merge each form definition with its per-form lead counts (by slug),
  // busiest first.
  const forms = useMemo(() => {
    const defs = Array.isArray(formList?.forms) ? formList.forms : [];
    const insForms = Array.isArray(insights?.forms) ? insights.forms : [];
    const bySlug = new Map(insForms.map((f) => [f.formSlug, f]));
    return defs
      .map((d) => ({ ...d, leads: bySlug.get(d.slug) || null }))
      .sort(
        (a, b) => (b.leads?.leadsWindow || 0) - (a.leads?.leadsWindow || 0)
      );
  }, [formList, insights]);

  // Keep the selected form valid as data loads / changes.
  useEffect(() => {
    if (!forms.length) return;
    if (!forms.some((f) => f.slug === selectedSlug)) {
      setSelectedSlug(forms[0].slug);
    }
  }, [forms, selectedSlug]);

  const selected =
    forms.find((f) => f.slug === selectedSlug) || forms[0] || null;

  const starts = funnel?.formStarts || 0;
  const sessions = funnel?.sessions || 0;
  // Authoritative leads (server-recorded FormSubmission, same window as the
  // funnel); client-event submits are kept only as a lossy engagement signal.
  // See discovery 14 (F-B).
  const leads = Number(funnel?.leads ?? funnel?.formSubmits) || 0;
  const dropped = Math.max(0, starts - leads);
  // Only show a start -> finish rate when starts is a credible denominator
  // (client form-start events undercount, so leads can exceed them); otherwise
  // fall back to the always-valid visitor -> lead rate.
  const completion = starts > 0 && leads <= starts ? pct(leads, starts) : null;
  const leadRate = pct(leads, sessions);
  const tooManyFields = selected && selected.fieldCount > 3;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2.5, sm: 3 } }}>
      <PageHeader
        title="Lead forms"
        meta={
          <Typography sx={{ fontSize: 13, color: t.muted, fontWeight: 600 }}>
            The front door for new work: site visit to form submission.
            {isDemo ? '  ·  Demo data' : ''}
          </Typography>
        }
        actions={
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="lf-range">Range</InputLabel>
            <Select
              labelId="lf-range"
              label="Range"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              disabled={isDemo}
            >
              {DAY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
            gap: { xs: 2, md: 2.5 },
            mt: 2,
            alignItems: 'start',
          }}
        >
          {/* Funnel column (site-wide) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <GradientHeroCard>
              <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>
                Form submissions · last {days} days
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1.5,
                  mt: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 38,
                    fontWeight: 900,
                    letterSpacing: '-1.2px',
                  }}
                >
                  {leads.toLocaleString()}
                </Typography>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, opacity: 0.9 }}
                >
                  leads
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, opacity: 0.85, mt: 0.5 }}>
                {completion != null
                  ? `${completion}% of the ${starts.toLocaleString()} visitors who started a form finished it.`
                  : `${leadRate}% of your ${sessions.toLocaleString()} site visitors became leads.`}
                {forms.length > 1 ? ' Across all forms.' : ''}
              </Typography>
            </GradientHeroCard>

            <Surface sx={{ p: 2.25 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '.8px',
                  textTransform: 'uppercase',
                  color: t.faint,
                  mb: 1.5,
                }}
              >
                From visit to form submission
              </Typography>
              <FunnelBar
                label="Visitors"
                value={funnel?.sessions || 0}
                max={funnel?.sessions || 0}
              />
              <FunnelBar
                label="Engaged (scrolled)"
                value={funnel?.scrolled75 || 0}
                max={funnel?.sessions || 0}
              />
              <FunnelBar
                label="Form starts"
                value={starts}
                max={funnel?.sessions || 0}
              />
              <FunnelBar
                label="Submissions"
                value={leads}
                max={funnel?.sessions || 0}
                color={t.green}
              />
              {dropped > 0 ? (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: t.amberBg,
                    border: `1px solid ${t.amberBorder}`,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12.5, color: t.amberText, fontWeight: 700 }}
                  >
                    Biggest leak: {dropped.toLocaleString()} started a form but
                    did not finish.
                    {tooManyFields
                      ? ' Shortening the form to 3 fields is the usual fix.'
                      : ''}
                  </Typography>
                </Box>
              ) : null}
            </Surface>
          </Box>

          {/* Forms column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!selected ? (
              <Surface sx={{ p: 2.25 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 800, color: t.text, mb: 0.5 }}
                >
                  Your contact form
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: t.muted }}>
                  No public form is live yet. Your UpRyzze setup team configures
                  the form(s) that go on your website.
                </Typography>
              </Surface>
            ) : (
              <Surface sx={{ p: 2.25 }}>
                {/* Form picker (only when there's more than one) */}
                {forms.length > 1 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      mb: 1.75,
                    }}
                  >
                    {forms.map((f) => {
                      const active = f.slug === selected.slug;
                      return (
                        <Box
                          key={f.slug}
                          component="button"
                          onClick={() => setSelectedSlug(f.slug)}
                          sx={{
                            cursor: 'pointer',
                            font: 'inherit',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: '11px',
                            py: '6px',
                            borderRadius: '999px',
                            border: `1px solid ${active ? t.blue : t.cardBorder}`,
                            bgcolor: active ? t.blueBg : t.card,
                            color: active ? t.blue : t.textSecondary,
                            fontSize: 12.5,
                            fontWeight: 700,
                          }}
                        >
                          {f.name}
                          {f.leads?.leadsTotal ? (
                            <Box
                              component="span"
                              sx={{ color: t.faint, fontWeight: 600 }}
                            >
                              {f.leads.leadsTotal}
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Box>
                ) : null}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 800, color: t.text }}
                  >
                    {selected.name}
                  </Typography>
                  <StatusPill
                    size="sm"
                    kind={selected.status === 'active' ? 'success' : 'neutral'}
                    label={selected.status === 'active' ? 'Live' : 'Off'}
                  />
                  {tooManyFields ? (
                    <Box sx={{ ml: 'auto' }}>
                      <StatusPill
                        size="sm"
                        kind="warning"
                        label={`${selected.fieldCount} fields`}
                      />
                    </Box>
                  ) : null}
                </Box>

                {/* Per-form funnel: started, leads, and how it converts */}
                {selected.leads &&
                (selected.leads.leadsTotal > 0 ||
                  (selected.leads.startsWindow || 0) > 0) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 3,
                      rowGap: 1.5,
                      mb: 1.75,
                    }}
                  >
                    <LeadStat
                      t={t}
                      value={(
                        selected.leads.startsWindow || 0
                      ).toLocaleString()}
                      caption={`started · ${days}d`}
                    />
                    <LeadStat
                      t={t}
                      value={(selected.leads.leadsWindow || 0).toLocaleString()}
                      caption={`leads · ${days}d`}
                    />
                    <LeadStat
                      t={t}
                      value={
                        selected.leads.conversionPct == null
                          ? 'n/a'
                          : `${selected.leads.conversionPct}%`
                      }
                      caption="finish rate"
                    />
                    <LeadStat
                      t={t}
                      value={(selected.leads.leadsTotal || 0).toLocaleString()}
                      caption="leads all time"
                    />
                    {selected.leads.lastSubmissionAt ? (
                      <LeadStat
                        t={t}
                        value={fmtLastSubmission(
                          selected.leads.lastSubmissionAt
                        )}
                        caption="last submission"
                      />
                    ) : null}
                  </Box>
                ) : null}

                {selected.fields.map((f) => (
                  <Box
                    key={f.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.75,
                      borderTop: `1px solid ${t.hairline}`,
                    }}
                  >
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        color: t.text,
                      }}
                      noWrap
                    >
                      {f.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: t.faint }}>
                      {FIELD_TYPE_LABEL[f.type] || f.type}
                    </Typography>
                    {f.required ? (
                      <StatusPill size="sm" kind="neutral" label="Required" />
                    ) : null}
                  </Box>
                ))}

                <Typography sx={{ fontSize: 11, color: t.faint, mt: 2 }}>
                  Form fields and notifications are managed by your UpRyzze
                  setup team.
                </Typography>
              </Surface>
            )}

            <Surface sx={{ p: 2.25 }}>
              <Typography
                sx={{ fontSize: 13, fontWeight: 800, color: t.text, mb: 0.5 }}
              >
                Submissions
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: t.muted, mb: 1.5 }}>
                Every submission lands in your Inbox, where you can reply and
                turn it into an estimate.
              </Typography>
              <Button
                startIcon={<InboxOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate(`${basePath}/inbox`)}
                sx={{
                  bgcolor: t.blue,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 12.5,
                  px: '14px',
                  py: '8px',
                  borderRadius: '9px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#1d4ed8' },
                }}
              >
                View submissions in Inbox
              </Button>
            </Surface>
          </Box>
        </Box>
      )}

      {/* Not tied to the page's loading state: changing the funnel's day
          range must not remount the card and reset its own week range. */}
      {!isDemo && selected ? <LeadTrendsCard formSlug={selected.slug} /> : null}
    </Container>
  );
}
